import React, { useCallback, useMemo, useEffect } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  Paper,
  Tooltip,
  IconButton,
  Divider
} from "@material-ui/core";
import { DirectionsCar, GpsFixed, Info, Help } from "@material-ui/icons";
import styled from "styled-components";

const StyledPaper = styled(Paper)`
  padding: 24px;
  margin-bottom: 16px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const TitleText = styled.div`
  font-weight: 600;
  color: #1976d2;
  font-size: 1.25rem;
  line-height: 1.4;
`;

const FormControlStyled = styled(FormControl)`
  width: 100%;
  margin-bottom: 16px;
`;

const VehicleInfo = styled.div`
  margin-top: 16px;
  padding: 16px;
  background-color: ${props => props.theme && props.theme.palette ? props.theme.palette.background.default : '#f5f5f5'};
  border-radius: 4px;
  border: 1px solid ${props => props.theme && props.theme.palette ? props.theme.palette.divider : '#e0e0e0'};
`;

const Alert = styled.div`
  padding: 16px;
  border-radius: 4px;
  margin-top: 16px;
  
  ${props => props.severity === 'warning' && `
    background-color: ${props.theme && props.theme.palette ? props.theme.palette.warning.light : '#fff3e0'};
    color: ${props.theme && props.theme.palette ? props.theme.palette.warning.contrastText : '#ef6c00'};
    border: 1px solid ${props.theme && props.theme.palette ? props.theme.palette.warning.main : '#ff9800'};
  `}
  
  ${props => props.severity === 'info' && `
    background-color: ${props.theme && props.theme.palette ? props.theme.palette.info.light : '#e3f2fd'};
    color: ${props.theme && props.theme.palette ? props.theme.palette.info.contrastText : '#1565c0'};
    border: 1px solid ${props.theme && props.theme.palette ? props.theme.palette.info.main : '#2196f3'};
  `}
`;

const DividerStyled = styled(Divider)`
  margin: 16px 0;
`;

/**
 * Componente optimizado para la selección de vehículos
 */
const VehicleSelection = React.memo(({
  vehicle,
  models,
  vehicles,
  gpsDevices,
  onVehicleChange,
  disabled = false,
  warnings = [],
  info = []
}) => {
  // Memoizar los modelos disponibles
  const availableModels = useMemo(() => {
    return models.filter(model => model.availability);
  }, [models]);

  // Memoizar los vehículos disponibles para el modelo seleccionado
  const availableVehicles = useMemo(() => {
    if (!vehicle.modelId) return [];
    return vehicles.filter(v => 
      v.model_id === vehicle.modelId && v.availability
    );
  }, [vehicles, vehicle.modelId]);

  // Memoizar los dispositivos GPS disponibles
  const availableGpsDevices = useMemo(() => {
    return gpsDevices.filter(gps => gps.availability);
  }, [gpsDevices]);

  // Autoseleccionar vehículo cuando ya hay un valor en el state
  useEffect(() => {
    // Si ya hay un vehículo seleccionado pero no está en la lista de vehículos disponibles
    if (vehicle.vehicleId && availableVehicles.length > 0) {
      const vehicleExists = availableVehicles.some(v => v.id === vehicle.vehicleId);
      
      // Si el vehículo seleccionado no existe en la lista actual, seleccionar el primero disponible
      if (!vehicleExists && availableVehicles.length > 0) {
        const firstAvailableVehicle = availableVehicles[0];
        onVehicleChange({
          ...vehicle,
          vehicleId: firstAvailableVehicle.id
        });
      }
    }
    // Si no hay vehículo seleccionado pero hay vehículos disponibles, seleccionar el primero
    else if (!vehicle.vehicleId && availableVehicles.length > 0) {
      const firstAvailableVehicle = availableVehicles[0];
      onVehicleChange({
        ...vehicle,
        vehicleId: firstAvailableVehicle.id
      });
    }
  }, [availableVehicles, vehicle.vehicleId, vehicle, onVehicleChange]);

  // Autoseleccionar GPS cuando ya hay un valor en el state
  useEffect(() => {
    // Si ya hay un GPS seleccionado pero no está en la lista de GPS disponibles
    if (vehicle.gpsDeviceId && availableGpsDevices.length > 0) {
      const gpsExists = availableGpsDevices.some(g => g.id === vehicle.gpsDeviceId);
      
      // Si el GPS seleccionado no existe en la lista actual, seleccionar el primero disponible
      if (!gpsExists && availableGpsDevices.length > 0) {
        const firstAvailableGps = availableGpsDevices[0];
        onVehicleChange({
          ...vehicle,
          gpsDeviceId: firstAvailableGps.id
        });
      }
    }
    // Si no hay GPS seleccionado pero hay GPS disponibles, seleccionar el primero
    else if (!vehicle.gpsDeviceId && availableGpsDevices.length > 0) {
      const firstAvailableGps = availableGpsDevices[0];
      onVehicleChange({
        ...vehicle,
        gpsDeviceId: firstAvailableGps.id
      });
    }
  }, [availableGpsDevices, vehicle.gpsDeviceId, vehicle, onVehicleChange]);

  // Callbacks optimizados para manejar cambios
  const handleModelChange = useCallback((event) => {
    onVehicleChange({
      ...vehicle,
      modelId: event.target.value,
      vehicleId: null, // Reset vehicle when model changes
      gpsDeviceId: null // Reset GPS when model changes
    });
  }, [vehicle, onVehicleChange]);

  const handleVehicleChange = useCallback((event) => {
    onVehicleChange({
      ...vehicle,
      vehicleId: event.target.value
    });
  }, [vehicle, onVehicleChange]);

  const handleGpsChange = useCallback((event) => {
    onVehicleChange({
      ...vehicle,
      gpsDeviceId: event.target.value
    });
  }, [vehicle, onVehicleChange]);

  // Obtener información del modelo seleccionado
  const selectedModel = useMemo(() => {
    return models.find(m => m.id === vehicle.modelId);
  }, [models, vehicle.modelId]);

  // Obtener información del vehículo seleccionado
  const selectedVehicle = useMemo(() => {
    return vehicles.find(v => v.id === vehicle.vehicleId);
  }, [vehicles, vehicle.vehicleId]);

  // Obtener información del GPS seleccionado
  const selectedGps = useMemo(() => {
    return gpsDevices.find(g => g.id === vehicle.gpsDeviceId);
  }, [gpsDevices, vehicle.gpsDeviceId]);

  return (
    <StyledPaper>
      <TitleContainer>
        <TitleText>
          Selección de Vehículo
        </TitleText>
        <Tooltip title="Selecciona el modelo y vehículo específico">
          <IconButton size="small">
            <Help fontSize="small" />
          </IconButton>
        </Tooltip>
      </TitleContainer>

                  <Grid container spacing={24}>
        {/* Selección de Modelo */}
        <Grid item xs={12} md={6}>
          <FormControlStyled>
            <InputLabel>Modelo de Vehículo</InputLabel>
            <Select
              value={vehicle.modelId || ""}
              onChange={handleModelChange}
              disabled={disabled}
              startAdornment={<DirectionsCar fontSize="small" style={{ marginRight: 8 }} />}
            >
              <MenuItem value="">
                <em>Seleccionar modelo</em>
              </MenuItem>
              {availableModels.map((model) => (
                <MenuItem key={model.id} value={model.id}>
                  {model.model_name}
                </MenuItem>
              ))}
            </Select>
          </FormControlStyled>
        </Grid>

        {/* Selección de Vehículo Específico */}
        {vehicle.modelId && availableVehicles.length > 0 && (
          <Grid item xs={12} md={6}>
            <FormControlStyled>
              <InputLabel>Vehículo Específico</InputLabel>
              <Select
                value={vehicle.vehicleId || ""}
                onChange={handleVehicleChange}
                disabled={disabled}
                startAdornment={<DirectionsCar fontSize="small" style={{ marginRight: 8 }} />}
              >
                <MenuItem value="">
                  <em>Seleccionar vehículo</em>
                </MenuItem>
                {availableVehicles.map((vehicleItem) => (
                  <MenuItem key={vehicleItem.id} value={vehicleItem.id}>
                    {vehicleItem.name || `Vehículo ${vehicleItem.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControlStyled>
          </Grid>
        )}

        {/* Selección de GPS */}
        <Grid item xs={12} md={6}>
          <FormControlStyled>
            <InputLabel>Dispositivo GPS</InputLabel>
            <Select
              value={vehicle.gpsDeviceId || ""}
              onChange={handleGpsChange}
              disabled={disabled}
              startAdornment={<GpsFixed fontSize="small" style={{ marginRight: 8 }} />}
            >
              <MenuItem value="">
                <em>Sin GPS</em>
              </MenuItem>
              {availableGpsDevices.map((gps) => (
                <MenuItem key={gps.id} value={gps.id}>
                  {gps.name || `GPS ${gps.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControlStyled>
        </Grid>
      </Grid>

      {/* Información del vehículo seleccionado */}
      {(selectedVehicle || selectedModel) && (
        <VehicleInfo>
          <Typography variant="subtitle2" gutterBottom>
            Información del Vehículo:
          </Typography>
          {selectedModel && (
            <Typography variant="body2" color="textSecondary">
              <strong>Modelo:</strong> {selectedModel.model_name}
            </Typography>
          )}
          {selectedVehicle && (
            <Typography variant="body2" color="textSecondary">
              <strong>Vehículo:</strong> {selectedVehicle.name || `Vehículo ${selectedVehicle.id}`}
            </Typography>
          )}
          {selectedGps && (
            <Typography variant="body2" color="textSecondary">
              <strong>GPS:</strong> {selectedGps.name || `GPS ${selectedGps.id}`}
            </Typography>
          )}
        </VehicleInfo>
      )}

      {/* Mostrar advertencias */}
      {warnings.length > 0 && (
        <Alert severity="warning">
          <Typography variant="body2">
            <strong>Advertencias:</strong>
          </Typography>
          {warnings.map((warning, index) => (
            <Typography key={index} variant="body2">
              • {warning}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Mostrar información */}
      {info.length > 0 && (
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Información:</strong>
          </Typography>
          {info.map((infoItem, index) => (
            <Typography key={index} variant="body2">
              • {infoItem}
            </Typography>
          ))}
        </Alert>
      )}

      <DividerStyled />
    </StyledPaper>
  );
});

VehicleSelection.displayName = 'VehicleSelection';

export default VehicleSelection; 


// WEBPACK FOOTER //
// ./src/components/tour/TourProcessing/components/VehicleSelection.js